'use client';

import React, { useState, Suspense } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  MessageSquare,
  Users,
  Kanban,
  ShoppingBag,
  FileText,
  Zap,
  Megaphone,
  Settings,
  ShieldAlert,
  LogOut,
  Archive,
  Lock,
  Globe,
  Download,
  Smile,
  Bot,
  CheckCircle2,
  Moon,
  Activity,
} from 'lucide-react';
import { StatusModal } from '@/components/presence/StatusModal';
import { usePwaInstall } from '@/components/pwa/PwaInstallPrompt';
import { useLocale } from 'next-intl';
import { useRouter as useI18nRouter, usePathname as useI18nPathname } from '@/i18n/routing';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { signOut } from '@/app/[locale]/(login)/actions';
import useSWR, { mutate } from 'swr';
import { User } from '@/lib/db/schema';
import { useInstanceStatus } from '@/lib/hooks/useInstanceStatus';
import { hasPermission, MemberPermissions } from '@/lib/permissions';
import Logo from '@/components/interface/Logo';
import { AddInstanceModal } from '@/components/instance/AddInstanceModal';
import { toast } from 'sonner';
import { Volume2 } from 'lucide-react';
import {
  playAlertRing,
  getSelectedSound,
  setSelectedSound as setStoreSelectedSound,
  getAlertRingEnabled,
  setAlertRingEnabled,
  NOTIFICATION_SOUNDS,
} from '@/lib/sound';
import { isMessagingWindowExpired } from '@/lib/chat-window';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

interface NavItemProps {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  badge?: string | number | null;
  hasDot?: boolean;
  locked?: boolean;
  lockedType?: 'instance' | 'permission';
  lockedTooltip?: string;
  onLockedClick?: () => void;
}

function RailButton({
  href,
  label,
  icon: Icon,
  active,
  badge,
  hasDot,
  locked,
  lockedType = 'instance',
  lockedTooltip,
  onLockedClick,
}: NavItemProps) {
  const content = (
    <div
      className={`size-10 rounded-xl flex items-center justify-center relative transition-colors ${
        locked
          ? 'cursor-pointer text-[#54656f] dark:text-[#aebac1] hover:text-[#111b21] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
          : active
          ? 'bg-black/10 dark:bg-white/10 text-[#111b21] dark:text-white font-medium'
          : 'text-[#54656f] dark:text-[#aebac1] hover:text-[#111b21] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
      }`}
      aria-label={label}
    >
      <Icon className="size-5" />
      {locked ? (
        <span
          className={`size-4 rounded-full ${
            lockedType === 'permission'
              ? 'bg-rose-500/90 text-white'
              : 'bg-amber-500/90 text-white'
          } flex items-center justify-center absolute -top-1 -right-1 shadow-2xs`}
        >
          <Lock className="size-2.5" />
        </span>
      ) : badge ? (
        <span className="bg-[#25d366] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-4.5 flex items-center justify-center absolute -top-1 -right-1 shadow-2xs">
          {badge}
        </span>
      ) : hasDot ? (
        <span className="size-2 rounded-full bg-[#25d366] absolute top-1.5 right-1.5 ring-2 ring-[#f0f2f5] dark:ring-[#202c33]" />
      ) : null}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {locked && lockedType === 'instance' ? (
          <button
            type="button"
            onClick={onLockedClick}
            className="outline-none focus:outline-none"
          >
            {content}
          </button>
        ) : (
          <Link href={href} className="outline-none focus:outline-none">
            {content}
          </Link>
        )}
      </TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={10}
        className="text-xs bg-[#111b21] dark:bg-[#202c33] text-white dark:text-[#e9edef] border-0 py-1 px-2.5 shadow-md rounded-md flex items-center gap-1.5"
      >
        <span>{label}</span>
        {locked && (
          <span
            className={`text-[10px] ${
              lockedType === 'permission' ? 'text-rose-400 font-medium' : 'text-amber-400 font-medium'
            }`}
          >
            {lockedTooltip || (lockedType === 'permission' ? '(Restricted for Role)' : '(Locked • Connect Instance)')}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { data: user } = useSWR<User>('/api/user', fetcher);
  const { data: chats } = useSWR<any[]>('/api/chats', fetcher);
  const { data: membership } = useSWR<{ role: string; teamId: number; permissions: MemberPermissions }>('/api/team/membership', fetcher);
  const { hasInstance, isLoading: isInstanceLoading } = useInstanceStatus();
  const [isAddInstanceOpen, setIsAddInstanceOpen] = useState(false);
  const [selectedSound, setSelectedSound] = useState('sound1');
  const [alertRingEnabled, setAlertRingEnabledState] = useState(true);

  const isOwner = membership?.role === 'owner';
  const roleName = membership?.role ? membership.role.charAt(0).toUpperCase() + membership.role.slice(1) : 'Member';

  const hasPerm = (resource: any) => {
    if (!membership) return true;
    if (isOwner) return true;
    return hasPermission(membership.role, membership.permissions, resource);
  };

  React.useEffect(() => {
    setSelectedSound(getSelectedSound());
    setAlertRingEnabledState(getAlertRingEnabled());

    const handleSoundChange = (e: any) => {
      if (e?.detail) setSelectedSound(String(e.detail));
    };
    const handleRingEnabledChange = (e: any) => {
      if (typeof e?.detail === 'boolean') setAlertRingEnabledState(e.detail);
    };

    window.addEventListener('selectedSoundChange', handleSoundChange);
    window.addEventListener('alertRingEnabledChange', handleRingEnabledChange);
    return () => {
      window.removeEventListener('selectedSoundChange', handleSoundChange);
      window.removeEventListener('alertRingEnabledChange', handleRingEnabledChange);
    };
  }, []);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const handleTogglePresence = async () => {
    const nextStatus = (user as any)?.presenceStatus === 'away' ? 'active' : 'away';
    try {
      const res = await fetch('/api/user/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presenceStatus: nextStatus,
          aiHandleChats: nextStatus === 'away' ? true : false,
        }),
      });
      if (res.ok) {
        await mutate('/api/user');
        await mutate('/api/user/presence');
        toast.success(
          nextStatus === 'away'
            ? 'Set as Away. AI agent will handle your chats.'
            : 'Set as Active. You will handle your chats.'
        );
      }
    } catch {}
  };

  const handleToneChange = (toneId: string) => {
    setSelectedSound(toneId);
    setStoreSelectedSound(toneId);
    playAlertRing(toneId, true);
  };

  const handleToggleRing = (checked: boolean) => {
    setAlertRingEnabledState(checked);
    setAlertRingEnabled(checked);
  };

  const pathnameWithoutLocale = pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
  const searchParams = useSearchParams();
  const currentTab = searchParams ? searchParams.get('tab') : null;
  const isArchived = currentTab === 'archived';

  function isActive(href: string) {
    if (href === '/dashboard') {
      return pathnameWithoutLocale === '/dashboard' || pathnameWithoutLocale.startsWith('/dashboard/');
    }
    return pathnameWithoutLocale.startsWith(href);
  }

  const [tick, setTick] = useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const { totalUnread, totalArchived } = React.useMemo(() => {
    if (!Array.isArray(chats)) return { totalUnread: 0, totalArchived: 0 };
    let unread = 0;
    let archived = 0;
    for (const c of chats) {
      if (isMessagingWindowExpired(c)) {
        archived++;
      } else {
        unread += (c.unreadCount || 0);
      }
    }
    return { totalUnread: unread, totalArchived: archived };
  }, [chats, tick]);

  async function handleSignOut() {
    await signOut();
    mutate('/api/user');
    router.push('/');
  }

  const { isInstalled, triggerInstall } = usePwaInstall();

  const locale = useLocale();
  const i18nRouter = useI18nRouter();
  const i18nPathname = useI18nPathname();
  const [isLanguagePending, startLanguageTransition] = React.useTransition();

  const handleLanguageChange = (nextLocale: string) => {
    startLanguageTransition(() => {
      i18nRouter.replace(i18nPathname, { locale: nextLocale });
    });
  };

  // When no instance exists and loading finished, lock features requiring instance
  const isFeatureLocked = !isInstanceLoading && !hasInstance;

  const handleLockedClick = (featureLabel: string) => {
    toast.warning('WhatsApp Instance Required', {
      description: `Connect a WhatsApp instance via WhatsApp Cloud API to unlock ${featureLabel}.`,
      action: {
        label: 'Add Instance',
        onClick: () => setIsAddInstanceOpen(true),
      },
    });
    setIsAddInstanceOpen(true);
  };

  const navItems = [
    {
      href: '/dashboard',
      label: 'Chats',
      icon: MessageSquare,
      active: (pathnameWithoutLocale === '/dashboard' || pathnameWithoutLocale.startsWith('/dashboard/chat')) && !isArchived,
      badge: totalUnread > 99 ? '99+' : totalUnread > 0 ? totalUnread : undefined,
      locked: false, // Dashboard / Chats is always unlocked!
    },
    {
      href: '/pipeline',
      label: 'Sales Pipeline',
      icon: Kanban,
      active: isActive('/pipeline'),
      locked: isFeatureLocked,
      lockedType: 'instance' as const,
      lockedTooltip: undefined,
      onLockedClick: () => handleLockedClick('Sales Pipeline'),
    },
    ...(isOwner ? [{
      href: '/monitoring',
      label: 'Team Monitoring',
      icon: Activity,
      active: isActive('/monitoring'),
      locked: false,
    }] : []),
    {
      href: '/orders',
      label: 'Orders',
      icon: ShoppingBag,
      active: isActive('/orders'),
      locked: !hasPerm('orders') || isFeatureLocked,
      lockedType: (!hasPerm('orders') ? 'permission' : 'instance') as 'permission' | 'instance',
      lockedTooltip: !hasPerm('orders') ? `(Restricted for ${roleName})` : undefined,
      onLockedClick: !hasPerm('orders') ? undefined : () => handleLockedClick('Orders'),
    },
    {
      href: '/contacts',
      label: 'Contacts',
      icon: Users,
      active: isActive('/contacts'),
      locked: !hasPerm('contacts') || isFeatureLocked,
      lockedType: (!hasPerm('contacts') ? 'permission' : 'instance') as 'permission' | 'instance',
      lockedTooltip: !hasPerm('contacts') ? `(Restricted for ${roleName})` : undefined,
      onLockedClick: !hasPerm('contacts') ? undefined : () => handleLockedClick('Contacts'),
    },
    {
      href: '/automation',
      label: 'Automation',
      icon: Zap,
      active: isActive('/automation'),
      locked: !hasPerm('automation') || isFeatureLocked,
      lockedType: (!hasPerm('automation') ? 'permission' : 'instance') as 'permission' | 'instance',
      lockedTooltip: !hasPerm('automation') ? `(Restricted for ${roleName})` : undefined,
      onLockedClick: !hasPerm('automation') ? undefined : () => handleLockedClick('Automation'),
    },
    ...(isOwner ? [{
      href: '/templates',
      label: 'WhatsApp Templates',
      icon: FileText,
      active: isActive('/templates'),
      locked: false,
    }] : []),
    {
      href: '/campaigns',
      label: 'Campaigns',
      icon: Megaphone,
      active: isActive('/campaigns'),
      locked: !hasPerm('campaigns') || isFeatureLocked,
      lockedType: (!hasPerm('campaigns') ? 'permission' : 'instance') as 'permission' | 'instance',
      lockedTooltip: !hasPerm('campaigns') ? `(Restricted for ${roleName})` : undefined,
      onLockedClick: !hasPerm('campaigns') ? undefined : () => handleLockedClick('Campaigns'),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <aside className="hidden md:flex w-[60px] min-w-[60px] bg-[#f0f2f5] dark:bg-[#202c33] border-r border-border/50 flex-col justify-between items-center py-3 h-full select-none shrink-0 z-30">
        {/* Top App Rail Icons */}
        <div className="flex flex-col items-center gap-2.5 w-full">
          {/* Zeric Brand Icon */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Link href="/dashboard" className="mb-2 focus:outline-none transition-transform hover:scale-105 active:scale-95">
                <Logo showName={false} size="sm" />
              </Link>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              className="text-xs bg-[#111b21] dark:bg-[#202c33] text-white dark:text-[#e9edef] border-0 py-1.5 px-2.5 shadow-md rounded-md flex flex-col"
            >
              <span className="font-bold">Zeric.link</span>
              <span className="text-[10px] text-emerald-400 font-medium">Let&apos;s sale automatic</span>
            </TooltipContent>
          </Tooltip>

          {navItems.map((item) => (
            <RailButton key={`${item.href}-${item.label}`} {...item} />
          ))}
        </div>

        {/* Bottom Rail Icons */}
        <div className="flex flex-col items-center gap-2.5 w-full pt-2">
          {user?.role === 'admin' && (
            <RailButton
              href="/admin"
              label="Admin Panel"
              icon={ShieldAlert}
              active={pathnameWithoutLocale.startsWith('/admin')}
            />
          )}

          {/* Settings icon: locked if role has no settings permission */}
          <RailButton
            href="/settings"
            label="Settings"
            icon={Settings}
            active={isActive('/settings')}
            locked={!isOwner && !hasPerm('settings')}
            lockedType="permission"
            lockedTooltip={!isOwner && !hasPerm('settings') ? `(Restricted for ${roleName})` : undefined}
          />

          {/* User Profile Avatar with Menu */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="size-9 rounded-full relative outline-none ring-offset-2 hover:ring-2 hover:ring-[#25d366] transition-all cursor-pointer mt-1 shrink-0"
                    aria-label="User profile"
                  >
                    <Avatar className="size-9">
                      <AvatarImage src={(user as any)?.avatarUrl || ''} alt={user?.name || user?.email || 'User'} />
                      <AvatarFallback className="bg-[#dfe5e7] dark:bg-[#374248] text-[#54656f] dark:text-[#aebac1] font-bold text-xs">
                        {(user?.name || user?.email || 'U').substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    {/* Slack-style Status Badge on Avatar */}
                    {(user as any)?.customStatusEmoji ? (
                      <span className="absolute -bottom-1 -right-1 size-4 rounded-full bg-background border border-border flex items-center justify-center text-[10px] leading-none shadow-xs">
                        {(user as any).customStatusEmoji}
                      </span>
                    ) : (user as any)?.presenceStatus === 'away' ? (
                      <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card bg-amber-500" title="Away" />
                    ) : (
                      <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card bg-emerald-500" title="Active" />
                    )}
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={10}
                className="text-xs bg-[#111b21] dark:bg-[#202c33] text-white dark:text-[#e9edef] border-0 py-1 px-2.5 shadow-md rounded-md"
              >
                Profile & Account
              </TooltipContent>
            </Tooltip>

            <DropdownMenuContent side="right" align="end" sideOffset={14} className="w-60 p-1.5 shadow-xl rounded-2xl bg-card border">
              {user && (
                <>
                  <DropdownMenuLabel className="font-normal px-2 py-1.5">
                    <p className="text-sm font-semibold text-foreground truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    <span className="inline-block mt-1 capitalize text-[10px] px-2 py-0.5 rounded-full bg-muted font-mono">
                      {user.role}
                    </span>
                  </DropdownMenuLabel>

                  {/* Slack-style Status preview card */}
                  <div className="mx-1 my-1 p-2 rounded-xl bg-muted/40 border border-border/50 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        {(user as any).presenceStatus === 'away' ? (
                          <>
                            <span className="size-2 rounded-full border border-amber-500 bg-amber-500/20" />
                            <span className="text-amber-600 dark:text-amber-400">Away</span>
                          </>
                        ) : (
                          <>
                            <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-emerald-600 dark:text-emerald-400">Active</span>
                          </>
                        )}
                        {(user as any).customStatusEmoji && <span>{(user as any).customStatusEmoji}</span>}
                      </div>
                      {(user as any).aiHandleChats && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                          <Bot className="size-2.5" /> AI
                        </span>
                      )}
                    </div>
                    {(user as any).customStatusText && (
                      <p className="text-[11px] text-muted-foreground truncate italic">
                        &ldquo;{(user as any).customStatusText}&rdquo;
                      </p>
                    )}
                  </div>

                  <DropdownMenuItem
                    onClick={handleTogglePresence}
                    className="cursor-pointer text-xs flex items-center gap-2 px-2 py-1.5 rounded-lg"
                  >
                    {(user as any).presenceStatus === 'away' ? (
                      <>
                        <CheckCircle2 className="size-3.5 text-emerald-500" />
                        <span>Set yourself as <strong>Active</strong></span>
                      </>
                    ) : (
                      <>
                        <Moon className="size-3.5 text-amber-500" />
                        <span>Set yourself as <strong>Away</strong></span>
                      </>
                    )}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => setIsStatusModalOpen(true)}
                    className="cursor-pointer text-xs flex items-center gap-2 px-2 py-1.5 rounded-lg"
                  >
                    <Smile className="size-3.5 text-blue-500" />
                    <span>Update your status</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer text-xs flex items-center justify-between px-2 py-1.5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Volume2 className="size-3.5 text-emerald-500" />
                    <span>Alert Tone</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground capitalize">
                    {NOTIFICATION_SOUNDS.find((s) => s.id === selectedSound)?.name || selectedSound}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-48 p-1 shadow-xl rounded-xl">
                  <DropdownMenuCheckboxItem
                    checked={alertRingEnabled}
                    onCheckedChange={handleToggleRing}
                    className="text-xs cursor-pointer"
                  >
                    Enable Alert Ring
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] text-muted-foreground font-normal px-2 py-1">
                    Choose Tone
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={selectedSound} onValueChange={handleToneChange}>
                    {NOTIFICATION_SOUNDS.map((sound) => (
                      <DropdownMenuRadioItem key={sound.id} value={sound.id} className="cursor-pointer text-xs">
                        {sound.name}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer text-xs flex items-center justify-between px-2 py-1.5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Globe className="size-3.5 text-blue-500" />
                    <span>Language</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {locale === 'ur' ? 'اردو' : locale === 'pt' ? 'Português' : locale === 'es' ? 'Español' : 'English'}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-40 p-1 shadow-xl rounded-xl">
                  <DropdownMenuRadioGroup value={locale} onValueChange={handleLanguageChange}>
                    <DropdownMenuRadioItem value="en" className="cursor-pointer text-xs">
                      English
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="ur" className="cursor-pointer text-xs">
                      اردو (Urdu)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="pt" className="cursor-pointer text-xs">
                      Português
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="es" className="cursor-pointer text-xs">
                      Español
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSeparator />

              {!isInstalled && (
                <>
                  <DropdownMenuItem
                    onClick={triggerInstall}
                    className="cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600 focus:bg-emerald-500/10 rounded-lg text-xs"
                  >
                    <Download className="size-3.5 mr-2" />
                    <span>Install App</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <div className="px-2 py-1.5 flex items-center justify-between">
                <span className="text-xs text-muted-foreground font-medium">Theme</span>
                <ThemeSwitcher />
              </div>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleSignOut}
                className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10 rounded-lg text-xs"
              >
                <LogOut className="size-3.5 mr-2" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <AddInstanceModal
        open={isAddInstanceOpen}
        onOpenChange={setIsAddInstanceOpen}
      />

      <StatusModal
        open={isStatusModalOpen}
        onOpenChange={setIsStatusModalOpen}
        user={user}
      />
    </TooltipProvider>
  );
}

export default Sidebar;

